import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Sparkles, Loader2, ShoppingCart, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Ailments that bath & body products can help with
const BODY_AILMENTS = [
  "Dry Skin",
  "Eczema",
  "Psoriasis",
  "Acne",
  "Oily Skin",
  "Sensitive Skin",
  "Aging Skin",
  "Wrinkles",
  "Stretch Marks",
  "Scars",
  "Inflammation",
  "Muscle Pain",
  "Joint Pain",
  "Arthritis",
  "Stress",
  "Anxiety",
  "Insomnia",
  "Poor Circulation",
  "Cellulite",
  "Uneven Skin Tone",
  "Dark Spots",
  "Rough Skin",
  "Cracked Skin",
  "Skin Irritation",
  "Redness",
  "Itchy Skin",
  "Body Odor",
  "Fungal Infections",
  "Minor Cuts & Wounds"
];

// JFRAI Fam Custom Scent Categories
const SCENT_CATEGORIES = {
  "Feminine Scents": [
    "Amazing Grace Essence", "Autumn Tangerine Bliss", "Champagne Toast Celebration",
    "Goddess Love", "Rose Romance", "Vanilla Seduction", "Pink Sugar Dreams",
    "Purple Passion Fire", "Lost Cherry Fantasy", "Black Opium Dreams"
  ],
  "Masculine Scents": [
    "Chrome Legend Power", "Black Knight Strength", "Aventus Victory",
    "Savage Instinct", "Oud Wood Mystery", "Tobacco Vanille Smooth",
    "Eros Passion", "Man Confidence", "Hero Spirit"
  ],
  "Natural & Unisex": [
    "Lavender Essential", "Eucalyptus Mint", "Vanilla Bean Classic",
    "Coconut Cream Paradise", "Lemongrass Fresh", "Sandalwood Vanilla",
    "Egyptian Musk Ancient", "Frankincense & Myrrh", "Unscented Pure"
  ]
};

export default function BodyBlendBuilderPage() {
  const navigate = useNavigate();
  const { user, fetchCartCount } = useContext(AuthContext);
  const [selectedAilments, setSelectedAilments] = useState([]);
  const [selectedScent, setSelectedScent] = useState('');
  const [preferences, setPreferences] = useState('');
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [blendName, setBlendName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAilmentToggle = (ailment) => {
    setSelectedAilments(prev =>
      prev.includes(ailment)
        ? prev.filter(a => a !== ailment)
        : [...prev, ailment]
    );
  };

  const generateRecommendation = async () => {
    if (!user) {
      toast.error('Please login to use the blend builder');
      navigate('/auth');
      return;
    }

    if (selectedAilments.length === 0) {
      toast.error('Please select at least one skin concern');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/body-blend/recommend`,
        {
          ailments: selectedAilments,
          preferences: preferences,
          preferred_scent: selectedScent
        },
        { withCredentials: true }
      );
      setRecommendation(response.data);
      setBlendName(response.data.recommendation.blend_name || 'My Custom Body Care Blend');
      toast.success('Body care recommendation generated!');
    } catch (error) {
      toast.error('Failed to generate recommendation');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const addBlendToCart = async () => {
    try {
      // Add each product in the blend to cart
      for (const product of recommendation.suggested_products) {
        await axios.post(
          `${API}/cart`,
          { item_id: product.id, item_type: 'product', quantity: 1 },
          { withCredentials: true }
        );
      }
      await fetchCartCount();
      toast.success('Blend added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <div 
      className="min-h-screen" 
      style={{ 
        backgroundImage: 'linear-gradient(rgba(0, 168, 107, 0.90), rgba(0, 168, 107, 0.90)), url(https://images.unsplash.com/photo-1556228841-4f71338a4a00?q=85)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif', textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }} data-testid="body-blend-builder-title">
            AI-Powered Body Care Blend Builder
          </h1>
          <p className="text-xl text-white" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Let our AI create the perfect combination of shea butter, soaps, and body care products for your skin needs
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Selection Panel */}
          <Card className="bg-white backdrop-blur-sm shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900 font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                <Sparkles className="inline w-6 h-6 mr-2 text-[#00A86B]" />
                Step 1: What skin concerns do you have?
              </CardTitle>
              <CardDescription className="text-gray-700 text-base">Select all that apply</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto" data-testid="ailments-list">
                {BODY_AILMENTS.map(ailment => (
                  <div key={ailment} className="flex items-center space-x-2">
                    <Checkbox
                      id={ailment}
                      checked={selectedAilments.includes(ailment)}
                      onCheckedChange={() => handleAilmentToggle(ailment)}
                      data-testid={`ailment-${ailment}`}
                      className="border-[#00A86B] data-[state=checked]:bg-[#00A86B] data-[state=checked]:text-white"
                    />
                    <Label htmlFor={ailment} className="cursor-pointer text-sm text-gray-900 font-medium">
                      {ailment}
                    </Label>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t">
                <Label htmlFor="scent-preference" className="text-base font-semibold mb-2 block text-gray-900">
                  Scent Preference (Optional)
                </Label>
                <select
                  id="scent-preference"
                  value={selectedScent}
                  onChange={(e) => setSelectedScent(e.target.value)}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="">No Preference</option>
                  {Object.entries(SCENT_CATEGORIES).map(([category, scents]) => (
                    <optgroup key={category} label={category}>
                      {scents.map(scent => (
                        <option key={scent} value={scent}>{scent}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <p className="text-sm text-gray-600 mt-1">Choose your favorite scent style</p>
              </div>

              <div className="pt-4 border-t">
                <Label htmlFor="preferences" className="text-base font-semibold mb-2 block text-gray-900">
                  Additional Preferences (Optional)
                </Label>
                <Textarea
                  id="preferences"
                  placeholder="E.g., I prefer unscented products, I have very sensitive skin, etc."
                  value={preferences}
                  onChange={(e) => setPreferences(e.target.value)}
                  className="min-h-24"
                  data-testid="preferences-input"
                />
              </div>

              <Button
                onClick={generateRecommendation}
                disabled={loading || selectedAilments.length === 0}
                className="w-full bg-[#00A86B] hover:bg-[#008f5d] text-lg py-6"
                data-testid="generate-recommendation-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Consulting AI Skin Expert...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Generate My Body Care Blend
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Recommendation Panel */}
          <Card className="bg-white backdrop-blur-sm shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl text-gray-900 font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your Custom Body Care Blend
              </CardTitle>
              <CardDescription className="text-gray-700 text-base">AI-generated recommendation based on your needs</CardDescription>
            </CardHeader>
            <CardContent>
              {!recommendation ? (
                <div className="text-center py-12 text-sage-600">
                  <Sparkles className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Select your skin concerns and generate a recommendation to see your custom body care blend here</p>
                </div>
              ) : (
                <div className="space-y-6" data-testid="recommendation-result">
                  <div>
                    <Label className="text-base font-semibold mb-2 block text-gray-900">Blend Name:</Label>
                    <Input
                      value={blendName}
                      onChange={(e) => setBlendName(e.target.value)}
                      className="text-lg"
                      data-testid="blend-name-input"
                    />
                  </div>

                  <div>
                    <Label className="text-base font-semibold mb-2 block text-gray-900">Why This Works:</Label>
                    <p className="text-gray-800 leading-relaxed bg-sage-50 p-4 rounded-lg">
                      {recommendation.recommendation.explanation}
                    </p>
                  </div>

                  <div>
                    <Label className="text-base font-semibold mb-2 block text-gray-900">Products in Your Blend:</Label>
                    <div className="space-y-3">
                      {recommendation.suggested_products.map(product => (
                        <div
                          key={product.id}
                          className="flex items-center gap-4 p-3 bg-cream-50 rounded-lg hover:bg-cream-100 transition-colors cursor-pointer"
                          data-testid={`blend-product-${product.id}`}
                        >
                          <img src={product.image_url} alt={product.name} className="w-16 h-16 rounded object-cover" />
                          <div className="flex-1">
                            <h4 className="font-semibold text-sage-900">{product.name}</h4>
                            <p className="text-sm text-sage-600">{product.type}</p>
                          </div>
                          <span className="text-lg font-bold text-terracotta-600">${product.price.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-base font-semibold mb-2 block text-gray-900">How to Use:</Label>
                    <p className="text-gray-800 bg-sage-50 p-4 rounded-lg">
                      {recommendation.recommendation.usage_instructions}
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={addBlendToCart}
                      className="flex-1 bg-[#00A86B] hover:bg-[#008f5d]"
                      data-testid="add-blend-to-cart-button"
                    >
                      <ShoppingCart className="mr-2 h-4 w-4" />
                      Add to Cart
                    </Button>
                  </div>

                  <div className="text-center pt-4 border-t">
                    <p className="text-sm text-sage-600">
                      Total: <span className="text-xl font-bold text-terracotta-600" data-testid="blend-total">
                        ${recommendation.suggested_products.reduce((sum, product) => sum + product.price, 0).toFixed(2)}
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Info Section */}
        <Card className="mt-12 bg-white backdrop-blur-sm shadow-2xl">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900 font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-24 h-24 bg-[#00A86B] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-4xl font-bold">
                  1
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Select Your Concerns</h3>
                <p className="text-base text-gray-700">Choose from 29 skin and body concerns</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-[#00A86B] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-4xl font-bold">
                  2
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">AI Analysis</h3>
                <p className="text-base text-gray-700">Our AI analyzes 50+ bath & body products to create your perfect blend</p>
              </div>
              <div className="text-center">
                <div className="w-24 h-24 bg-[#00A86B] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-4xl font-bold">
                  3
                </div>
                <h3 className="font-semibold text-gray-900 mb-2 text-lg">Order & Enjoy</h3>
                <p className="text-base text-gray-700">Add to cart and start your personalized skin care journey</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
